import React, { useCallback, useEffect, useMemo, useState } from "react";
import api from "../../../api/axios";
import "./DesviosNegarPage.css";

import CriarDesvioNegarModal from "./popups/CriarDesvioNegarModal.jsx";
import EditarDesvioNegarModal from "./popups/EditarDesvioNegarModal.jsx";
import AtivarDesvioNegarModal from "./popups/AtivarDesvioNegarModal.jsx";
import InativarDesvioNegarModal from "./popups/InativarDesvioNegarModal.jsx";
import ExcluirDesvioNegarModal from "./popups/ExcluirDesvioNegarModal.jsx";

const UP = (v) => (typeof v === "string" ? v.toUpperCase() : v ?? "");

function IconEdit(props) {
  return (
    <svg
      viewBox="0 0 24 24"
      width="18"
      height="18"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      {...props}
    >
      <path d="M12 20h9" />
      <path d="M16.5 3.5a2.121 2.121 0 1 1 3 3L7 19l-4 1 1-4 12.5-12.5z" />
    </svg>
  );
}

function IconTrash(props) {
  return (
    <svg
      viewBox="0 0 24 24"
      width="18"
      height="18"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      {...props}
    >
      <polyline points="3 6 5 6 21 6" />
      <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
      <path d="M10 11v6M14 11v6" />
      <path d="M9 6V4a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2" />
    </svg>
  );
}

function ToggleSwitch({ on = false, onToggle, title }) {
  return (
    <button
      type="button"
      aria-pressed={on}
      className={`dve-toggle ${on ? "on" : "off"}`}
      onClick={onToggle}
      title={title || (on ? "Inativar" : "Ativar")}
    >
      <span className="knob" />
    </button>
  );
}

async function apiList(params) {
  try {
    return await api.get("/desvios_negar/listar", { params });
  } catch {
    return await api.get("/bases/desvios_negar/listar", { params });
  }
}

async function apiCreate(body) {
  try {
    return await api.post("/desvios_negar/cadastrar", body);
  } catch {
    return await api.post("/bases/desvios_negar/cadastrar", body);
  }
}

async function apiUpdate(id, body) {
  try {
    return await api.put(`/desvios_negar/editar/${id}`, body);
  } catch {
    return await api.put(`/bases/desvios_negar/editar/${id}`, body);
  }
}

async function apiToggle(id, ativo) {
  try {
    return await api.patch(`/desvios_negar/alterar-ativo/${id}`, { ativo });
  } catch {
    return await api.patch(`/bases/desvios_negar/alterar-ativo/${id}`, { ativo });
  }
}

async function apiRemove(id) {
  try {
    return await api.delete(`/desvios_negar/excluir/${id}`);
  } catch {
    return await api.delete(`/bases/desvios_negar/excluir/${id}`);
  }
}

async function apiToggleCausador(causador, ativo) {
  try {
    return await api.patch("/desvios_negar/alterar-ativo-causador", { causador, ativo });
  } catch {
    return await api.patch("/bases/desvios_negar/alterar-ativo-causador", { causador, ativo });
  }
}

async function apiRemoveCausador(causador) {
  try {
    return await api.delete("/desvios_negar/excluir-causador", { params: { causador } });
  } catch {
    return await api.delete("/bases/desvios_negar/excluir-causador", { params: { causador } });
  }
}

function sortRows(a, b) {
  const ca = String(a?.causador ?? "").localeCompare(String(b?.causador ?? ""), "pt-BR", {
    sensitivity: "base",
  });
  if (ca !== 0) return ca;

  const cp = String(a?.causa_primaria ?? "").localeCompare(String(b?.causa_primaria ?? ""), "pt-BR", {
    sensitivity: "base",
  });
  if (cp !== 0) return cp;

  const co = String(a?.codigo ?? "").localeCompare(String(b?.codigo ?? ""), "pt-BR", {
    sensitivity: "base",
  });
  if (co !== 0) return co;

  return Number(a?.id ?? 0) - Number(b?.id ?? 0);
}

export default function DesviosNegarPage({ onBack }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  const [grupoFiltro, setGrupoFiltro] = useState(null);

  const [openCreate, setOpenCreate] = useState(false);
  const [createSeed, setCreateSeed] = useState(null);
  const [editRow, setEditRow] = useState(null);

  const [confirm, setConfirm] = useState({
    open: false,
    kind: null,
    payload: null,
  });

  useEffect(() => {
    document.body.classList.add("dven-skin");
    return () => document.body.classList.remove("dven-skin");
  }, []);

  const stats = useMemo(() => {
    const total = rows.length;
    const ativos = rows.filter((r) => Number(r.ativo)).length;
    const inativos = total - ativos;

    const porGrupo = {};
    for (const r of rows) {
      const g = UP(r?.causador || "-");
      porGrupo[g] = (porGrupo[g] || 0) + 1;
    }
    const chips = Object.entries(porGrupo).sort((a, b) =>
      a[0].localeCompare(b[0], "pt-BR", { sensitivity: "base" })
    );

    return { total, ativos, inativos, chips };
  }, [rows]);

  const carregar = useCallback(async () => {
    setLoading(true);
    setErr("");
    try {
      const params = { limit: 1000, offset: 0 };
      const r = await apiList(params);
      const data = r?.data?.data ?? r?.data ?? [];
      setRows(Array.isArray(data) ? [...data].sort(sortRows) : []);
    } catch (e) {
      setErr(e?.response?.data?.error || e.message || "Erro ao listar");
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    carregar();
  }, [carregar]);

  const agrupado = useMemo(() => {
    const map = {};
    const filtro = grupoFiltro ? UP(grupoFiltro) : null;

    for (const r of rows) {
      const causador = UP(r?.causador || "-");
      if (filtro && causador !== filtro) continue;
      map[causador] ??= [];
      map[causador].push(r);
    }

    for (const causador of Object.keys(map)) {
      map[causador].sort(sortRows);
    }

    return map;
  }, [rows, grupoFiltro]);

  const causadoresVisiveis = useMemo(
    () => Object.keys(agrupado).sort((a, b) => a.localeCompare(b, "pt-BR", { sensitivity: "base" })),
    [agrupado]
  );

  function goHome() {
    if (onBack) onBack();
    else if (window.history.length > 1) window.history.back();
    else window.location.href = "/";
  }

  function buildRowsForCausador(items = []) {
    const byPrimaria = {};
    for (const row of items) {
      const primaria = UP(row?.causa_primaria || "-");
      byPrimaria[primaria] ??= [];
      byPrimaria[primaria].push(row);
    }

    const primarias = Object.keys(byPrimaria).sort((a, b) =>
      a.localeCompare(b, "pt-BR", { sensitivity: "base" })
    );

    const output = [];
    primarias.forEach((primaria, idx) => {
      const list = byPrimaria[primaria];
      if (idx > 0) output.push({ _sep: true, _key: `sep-${primaria}-${idx}` });
      list.forEach((row, line) => {
        output.push({
          ...row,
          _primaria: primaria,
          _showPrimaria: line === 0,
          _span: list.length,
        });
      });
    });

    return output;
  }

  function askToggle(row) {
    const isOn = Number(row.ativo) === 1;
    const label = [row.causador, row.causa_primaria].filter(Boolean).join(" / ");
    setConfirm({
      open: true,
      kind: isOn ? "inativar" : "ativar",
      payload: { id: row.id, label: label || row.codigo || `#${row.id}` },
    });
  }

  function isCausadorAtivo(causador) {
    const itens = agrupado[causador] || [];
    return itens.some((r) => Number(r?.ativo) === 1);
  }

  function askToggleCausador(causador) {
    const ativo = isCausadorAtivo(causador);
    setConfirm({
      open: true,
      kind: ativo ? "inativar-causador" : "ativar-causador",
      payload: { causador, label: causador },
    });
  }

  function askDeleteCausador(causador) {
    setConfirm({
      open: true,
      kind: "excluir-causador",
      payload: { causador, label: causador },
    });
  }

  function askDelete(row) {
    const label = [row.causador, row.causa_primaria].filter(Boolean).join(" / ");
    setConfirm({
      open: true,
      kind: "excluir",
      payload: { id: row.id, label: label || row.codigo || `#${row.id}` },
    });
  }

  function microUpsert(updated) {
    setRows((prev) => {
      const next = [...prev];
      const i = next.findIndex((x) => Number(x.id) === Number(updated.id));
      if (i >= 0) next[i] = { ...next[i], ...updated };
      else next.push(updated);
      next.sort(sortRows);
      return next;
    });
  }

  function microRemove(id) {
    setRows((prev) => prev.filter((x) => Number(x.id) !== Number(id)));
  }

  function microSetCausadorAtivo(causador, ativo) {
    setRows((prev) =>
      prev.map((row) =>
        UP(row?.causador || "-") === UP(causador) ? { ...row, ativo: Number(ativo) ? 1 : 0 } : row
      )
    );
  }

  function microRemoveCausador(causador) {
    setRows((prev) => prev.filter((row) => UP(row?.causador || "-") !== UP(causador)));
  }

  async function handleConfirm() {
    const { kind, payload } = confirm || {};
    if (!kind) return;

    try {
      if (kind === "ativar") {
        if (!payload?.id) return;
        await apiToggle(payload.id, 1);
        microUpsert({ id: payload.id, ativo: 1 });
      }
      if (kind === "inativar") {
        if (!payload?.id) return;
        await apiToggle(payload.id, 0);
        microUpsert({ id: payload.id, ativo: 0 });
      }
      if (kind === "excluir") {
        if (!payload?.id) return;
        await apiRemove(payload.id);
        microRemove(payload.id);
      }
      if (kind === "ativar-causador") {
        if (!payload?.causador) return;
        await apiToggleCausador(payload.causador, 1);
        microSetCausadorAtivo(payload.causador, 1);
      }
      if (kind === "inativar-causador") {
        if (!payload?.causador) return;
        await apiToggleCausador(payload.causador, 0);
        microSetCausadorAtivo(payload.causador, 0);
      }
      if (kind === "excluir-causador") {
        if (!payload?.causador) return;
        await apiRemoveCausador(payload.causador);
        microRemoveCausador(payload.causador);
      }
      setConfirm({ open: false, kind: null, payload: null });
    } catch (e) {
      alert(e?.response?.data?.error || e.message || "Erro ao executar acao");
    }
  }

  async function handleCreateSubmit(form) {
    try {
      const body = {
        causador: UP(form.causador || ""),
        causa_primaria: UP(form.causa_primaria || ""),
        quando_utilizado: String(form.quando_utilizado || "").trim() || null,
        ativo: Number(form.ativo),
      };

      if (!body.causador || !body.causa_primaria || (body.ativo !== 0 && body.ativo !== 1)) {
        return alert("Preencha os campos obrigatorios: Causador, Causa Primaria e Ativo.");
      }

      const existente = rows.find(
        (r) =>
          UP(r?.causador || "") === body.causador &&
          UP(r?.causa_primaria || "") === body.causa_primaria
      );

      if (existente?.id) {
        const { data } = await apiUpdate(existente.id, body);
        const atualizado = data?.data ?? data;
        if (atualizado?.id) microUpsert(atualizado);
        else await carregar();
        setOpenCreate(false);
        return;
      }

      const { data } = await apiCreate(body);
      const r = data?.data ?? data;
      if (r?.id) microUpsert(r);
      else await carregar();

      setOpenCreate(false);
    } catch (e) {
      alert(e?.response?.data?.error || "Erro ao cadastrar");
    }
  }

  async function handleEditSubmit(form) {
    try {
      const id = form?.id;
      if (!id) return;

      const body = {
        causador: UP(form.causador || ""),
        causa_primaria: UP(form.causa_primaria || ""),
        quando_utilizado: String(form.quando_utilizado || "").trim() || null,
        ativo: Number(form.ativo),
      };

      if (!body.causador || !body.causa_primaria || (body.ativo !== 0 && body.ativo !== 1)) {
        return alert("Preencha os campos obrigatorios: Causador, Causa Primaria e Ativo.");
      }

      const { data } = await apiUpdate(id, body);
      const r = data?.data ?? data;
      if (r?.id) microUpsert(r);
      else await carregar();

      setEditRow(null);
    } catch (e) {
      alert(e?.response?.data?.error || "Erro ao editar");
    }
  }

  return (
    <div className="dve-shell">
      <div className="dve-topbar">
        <button className="dve-btn-hero left" type="button" onClick={goHome}>
          Voltar para o inicio
        </button>

        <h1 className="dve-title">Catálago de Desvios — Cancelamento</h1>

        <button
          className="dve-btn-hero right"
          type="button"
          onClick={() => {
            setCreateSeed(null);
            setOpenCreate(true);
          }}
        >
          Cadastrar
        </button>
      </div>

      <div className="dve-head">
        <div className="dve-cards-row dve-cards--all">
          <div className="dve-card">
            <div className="dve-card__label">TOTAL</div>
            <div className="dve-card__value">{stats.total}</div>
          </div>
          <div className="dve-card dve-card--ok">
            <div className="dve-card__label">ATIVOS</div>
            <div className="dve-card__value">{stats.ativos}</div>
          </div>
          <div className="dve-card dve-card--alert">
            <div className="dve-card__label">INATIVOS</div>
            <div className="dve-card__value">{stats.inativos}</div>
          </div>

          <div className="dve-chips-panel">
            <div className="dve-chips-panel__label">POR GRUPO</div>
            <div className="dve-chip-list">
              {stats.chips.map(([g, q]) => (
                <button
                  key={g}
                  className={`dve-chip ${grupoFiltro && UP(grupoFiltro) === UP(g) ? "active" : ""}`}
                  type="button"
                  onClick={() => setGrupoFiltro((prev) => (prev && UP(prev) === UP(g) ? null : g))}
                  title={`Filtrar por ${g}`}
                >
                  {g}
                  <span className="dve-chip__num">{q}</span>
                </button>
              ))}
              {grupoFiltro && (
                <button
                  className="dve-chip dve-chip--clear"
                  type="button"
                  onClick={() => setGrupoFiltro(null)}
                  title="Limpar filtro"
                >
                  Limpar filtro x
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="dve-wrap">
        {loading ? (
          <div className="dve-empty">Carregando...</div>
        ) : err ? (
          <div className="dve-empty">{err}</div>
        ) : causadoresVisiveis.length === 0 ? (
          <div className="dve-empty">Nenhum item encontrado.</div>
        ) : (
          causadoresVisiveis.map((causador) => {
            const itens = agrupado[causador] || [];
            const linhas = buildRowsForCausador(itens);

            return (
              <section className="dve-group" key={causador}>
                <header className="dve-group__head">
                  <button
                    type="button"
                    className="dve-btn-lite"
                    onClick={() => {
                      setCreateSeed({ causador });
                      setOpenCreate(true);
                    }}
                    title={`Cadastrar nova causa primaria em ${causador}`}
                  >
                    Cadastrar
                  </button>

                  <h2 className="dve-causador">{causador}</h2>
                  <div className="dve-head-actions">
                    <ToggleSwitch
                      on={isCausadorAtivo(causador)}
                      onToggle={() => askToggleCausador(causador)}
                      title={isCausadorAtivo(causador) ? "Inativar causador" : "Ativar causador"}
                    />
                    <span className="dve-actions-divider" aria-hidden="true" />
                    <button
                      className="dve-icon-btn"
                      title="Excluir causador"
                      onClick={() => askDeleteCausador(causador)}
                    >
                      <IconTrash />
                    </button>
                  </div>
                </header>

                <div className="dve-table-wrap">
                  <table className="dve-table">
                    <colgroup>
                      <col style={{ width: "36%" }} />
                      <col style={{ width: "48%" }} />
                      <col style={{ width: "16%" }} />
                    </colgroup>
                    <thead>
                      <tr>
                        <th>CAUSA PRIMARIA</th>
                        <th>QUANDO UTILIZADO</th>
                        <th>CONTROLES</th>
                      </tr>
                    </thead>
                    <tbody>
                      {linhas.length === 0 ? (
                        <tr>
                          <td className="dve-td" colSpan={3}>
                            Nenhuma causa primaria.
                          </td>
                        </tr>
                      ) : (
                        linhas.map((row) =>
                          row._sep ? (
                            <tr key={row._key}>
                              <td colSpan={3} className="dve-sep" />
                            </tr>
                          ) : (
                            <tr key={row.id}>
                              {row._showPrimaria && (
                                <td className="dve-td dve-td-strong dve-td-primaria" rowSpan={row._span}>
                                  {row._primaria}
                                </td>
                              )}
                              <td className="dve-td dve-td-left">{row.quando_utilizado || "-"}</td>
                              <td className="dve-td dve-td-controls">
                                <div className="dve-actions dve-actions--cluster">
                                  <ToggleSwitch
                                    on={!!Number(row.ativo)}
                                    onToggle={() => askToggle(row)}
                                    title={Number(row.ativo) ? "Inativar" : "Ativar"}
                                  />
                                  <span className="dve-actions-divider" aria-hidden="true" />
                                  <button
                                    className="dve-icon-btn"
                                    title="Editar"
                                    onClick={() =>
                                      setEditRow({
                                        ...row,
                                        causador,
                                        causa_primaria: row._primaria || row.causa_primaria,
                                      })
                                    }
                                  >
                                    <IconEdit />
                                  </button>
                                  <span className="dve-actions-divider" aria-hidden="true" />
                                  <button className="dve-icon-btn" title="Excluir" onClick={() => askDelete(row)}>
                                    <IconTrash />
                                  </button>
                                </div>
                              </td>
                            </tr>
                          )
                        )
                      )}
                    </tbody>
                  </table>
                </div>
              </section>
            );
          })
        )}
      </div>

      {openCreate && (
        <CriarDesvioNegarModal
          open
          initial={{
            causador: createSeed?.causador ?? "",
            causa_primaria: "",
            quando_utilizado: "",
            ativo: "",
          }}
          onClose={() => {
            setOpenCreate(false);
            setCreateSeed(null);
          }}
          onSubmit={async (form) => {
            await handleCreateSubmit(form);
            setCreateSeed(null);
          }}
        />
      )}

      {editRow && (
        <EditarDesvioNegarModal
          open
          initial={{
            id: editRow.id,
            causador: editRow.causador ?? "",
            causa_primaria: editRow.causa_primaria ?? "",
            quando_utilizado: editRow.quando_utilizado ?? "",
            ativo: Number(editRow.ativo) ? 1 : 0,
          }}
          onClose={() => setEditRow(null)}
          onSubmit={handleEditSubmit}
        />
      )}

      {confirm.open && (confirm.kind === "ativar" || confirm.kind === "ativar-causador") && (
        <AtivarDesvioNegarModal
          open
          label={UP(confirm.payload?.label)}
          onClose={() => setConfirm({ open: false, kind: null, payload: null })}
          onConfirm={handleConfirm}
        />
      )}

      {confirm.open && (confirm.kind === "inativar" || confirm.kind === "inativar-causador") && (
        <InativarDesvioNegarModal
          open
          label={UP(confirm.payload?.label)}
          onClose={() => setConfirm({ open: false, kind: null, payload: null })}
          onConfirm={handleConfirm}
        />
      )}

      {confirm.open && (confirm.kind === "excluir" || confirm.kind === "excluir-causador") && (
        <ExcluirDesvioNegarModal
          open
          label={UP(confirm.payload?.label)}
          onClose={() => setConfirm({ open: false, kind: null, payload: null })}
          onConfirm={handleConfirm}
        />
      )}
    </div>
  );
}
